// "✓ Reached out" — one-tap manual outreach logging (outreach touches spec).
// Inserts an outreach_touches row (channel 'direct_text' | 'nurse', optional
// note), optionally a manual client_reminders follow-up when a date is given,
// and — when opened from an AI recommendation card (recId set) — flips that
// ai_recommendations row to status='done' after the touch insert.
//
// CRM texts are never logged here: outbound `messages` rows count as touches
// automatically inside the database view (v_outreach_today, channel
// 'crm_text'), so this modal only covers contact made OUTSIDE the CRM.

import { useState } from "react";
import { supabase } from "../lib/supabase";
import Modal from "./Modal";
import type { Id, TouchChannel } from "../lib/types";

/** Today's business date (YYYY-MM-DD, America/New_York). */
function nyTodayYmd(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** "today" / "5d ago" from a timestamptz; "" for null/invalid. */
export function daysAgoLabel(ts: string | null | undefined): string {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  return `${days}d ago`;
}

/** Pastel pill for a touch channel: "✓ you — direct" / "✓ nurse" / "crm text". */
export function TouchChannelBadge({ channel }: { channel: TouchChannel | string | null }) {
  if (channel === "direct_text") return <span className="badge badge-mint">✓ you — direct</span>;
  if (channel === "nurse") return <span className="badge badge-sky">✓ nurse</span>;
  if (channel === "crm_text") return <span className="badge badge-muted">crm text</span>;
  return null;
}

export interface MarkReachedOutProps {
  clientId: Id;
  clientName: string;
  /** Called after a touch was saved (also on partial failures past the insert). */
  onDone?: () => void;
  /** When set, this ai_recommendations row is set to status='done' after the touch. */
  recId?: string;
  /** Trigger-button classes; defaults to the small ghost button used in action rows. */
  className?: string;
}

export default function MarkReachedOut({
  clientId,
  clientName,
  onDone,
  recId,
  className,
}: MarkReachedOutProps) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<Extract<TouchChannel, "direct_text" | "nurse"> | null>(
    null,
  );
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // clients.id is integer in the live DB; route params arrive as strings.
  const idArg: Id =
    typeof clientId === "string" && /^\d+$/.test(clientId) ? Number(clientId) : clientId;

  const today = nyTodayYmd();

  function reset() {
    setChannel(null);
    setNote("");
    setFollowUp("");
    setError(null);
  }

  function close() {
    if (busy) return;
    setOpen(false);
    reset();
  }

  async function save() {
    if (!channel || busy) return;
    setBusy(true);
    setError(null);
    let touchSaved = false;
    try {
      const { error: touchError } = await supabase.from("outreach_touches").insert({
        client_id: idArg,
        channel,
        note: note.trim() || null,
      });
      if (touchError) throw new Error(touchError.message);
      touchSaved = true;

      if (followUp) {
        const remNote =
          "Follow up — " +
          (note.trim() || (channel === "nurse" ? "nurse reached out" : "direct text"));
        const { error: remError } = await supabase.from("client_reminders").insert({
          client_id: idArg,
          due_date: followUp,
          note: remNote,
          source: "manual",
        });
        if (remError) {
          throw new Error(`Touch saved, but the follow-up reminder failed: ${remError.message}`);
        }
      }

      if (recId) {
        const { error: recError } = await supabase
          .from("ai_recommendations")
          .update({ status: "done" })
          .eq("id", recId);
        if (recError) {
          throw new Error(`Touch saved, but marking the AI pick done failed: ${recError.message}`);
        }
      }

      setOpen(false);
      reset();
      onDone?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // The touch row exists even when a follow-up step failed — let the
      // caller reload so the lists reflect the logged touch.
      if (touchSaved) onDone?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={className ?? "btn btn-ghost btn-sm"}
        onClick={() => {
          reset();
          setOpen(true);
        }}
        title="Log that you or a nurse reached out (outside the CRM)"
      >
        ✓ Reached out
      </button>

      <Modal
        open={open}
        title={`Reached out to ${clientName}`}
        onClose={close}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void save()}
              disabled={busy || !channel}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        {error && <div className="error-banner">{error}</div>}

        <div className="channel-choices">
          <button
            type="button"
            className={"channel-choice" + (channel === "direct_text" ? " selected" : "")}
            onClick={() => setChannel("direct_text")}
            aria-pressed={channel === "direct_text"}
          >
            <span className="channel-choice-emoji">💬</span>
            <span className="channel-choice-label">Me — direct text</span>
            <span className="channel-choice-sub">I texted or called them myself</span>
          </button>
          <button
            type="button"
            className={"channel-choice" + (channel === "nurse" ? " selected" : "")}
            onClick={() => setChannel("nurse")}
            aria-pressed={channel === "nurse"}
          >
            <span className="channel-choice-emoji">🩺</span>
            <span className="channel-choice-label">Nurse reached out</span>
            <span className="channel-choice-sub">A nurse contacted them</span>
          </button>
        </div>

        <div className="field">
          <label htmlFor="touch-note">Note (optional)</label>
          <input
            id="touch-note"
            value={note}
            placeholder="e.g. left a voicemail about the 5-pack"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="touch-followup">Follow up on (optional)</label>
          <input
            id="touch-followup"
            type="date"
            min={today}
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
          />
          <div className="field-hint">Creates a reminder for that date.</div>
        </div>

        <div className="field-hint">
          Texts sent from the CRM count automatically — only log contact made outside it.
        </div>
      </Modal>
    </>
  );
}
